/**
 * pasteTasks — the one paste path for the ⋯ menus and Cmd+V.
 *
 * Every line of the pasted text becomes a task, created with the ATOMIC
 * addTask (parent, schedule row and index in one transaction) using the
 * view's defaults from getTaskDefaults, exactly like QuickAdd and the FAB.
 * That puts a task pasted in Today, Evening, Upcoming or Someday in that view,
 * and the same default is what the other creation paths use.
 *
 * Order: each task is inserted at the same position ("top" or after a given
 * task), so the LAST insert ends up first. The titles are therefore inserted
 * in reverse, which leaves the list in the clipboard's order.
 */

import i18n from "i18next"
import { toast } from "sonner"
import { addTask } from "@/db/operations"
import type { InsertPosition } from "@/db/operations"
import type { ViewId } from "@/features/views/viewRegistry"
import { readClipboard } from "@/shared/utils/platform/clipboard"
import { parseClipboardLines } from "@/shared/utils/clipboard/parseClipboardLines"
import { getTaskDefaults } from "@/shared/utils/task/getTaskDefaults"
import { orderForSamePositionInsert } from "@/shared/utils/task/pasteOrder"

export interface PasteTasksParams {
  viewId: ViewId
  containerId?: string | null
  eveningHour?: number
  /** Default "top" (right under the quick-add row); Cmd+V with a selected task passes `{ afterId }`. */
  insertPosition?: InsertPosition
}

export interface PasteTasksResult {
  created: number
  failed: number
}

/** Create one task per title, in the given order. Never throws; reports counts. */
export async function createTasksFromTitles(
  titles: readonly string[],
  params: PasteTasksParams,
): Promise<PasteTasksResult> {
  const defaults = getTaskDefaults(
    params.viewId,
    params.containerId ?? undefined,
    params.eveningHour,
  )
  let created = 0
  let failed = 0
  for (const title of orderForSamePositionInsert(titles)) {
    try {
      const result = await addTask({
        title,
        parentPath: defaults.parentPath,
        insertPosition: params.insertPosition ?? "top",
        startAt: defaults.schedule?.startAt,
        startAtType: defaults.schedule?.type,
        startAtSubType: defaults.schedule?.subType,
      })
      if (result.ok) created++
      else failed++
    } catch (error) {
      console.error("[pasteTasks] addTask failed:", error)
      failed++
    }
  }
  return { created, failed }
}

function toastResult({ created, failed }: PasteTasksResult, firstTitle: string | undefined): void {
  if (failed > 0) {
    toast.error(i18n.t("Failed to paste tasks"))
    return
  }
  if (created === 1 && firstTitle) {
    toast(i18n.t("Task created: {{title}}", { title: firstTitle }))
  } else if (created > 1) {
    toast(i18n.t("{{count}} tasks created", { count: created }))
  }
}

/** Paste already-read text (Cmd+V). One toast for the whole paste. */
export async function pasteTasksFromText(
  text: string,
  params: PasteTasksParams,
): Promise<PasteTasksResult> {
  const titles = parseClipboardLines(text)
  if (titles.length === 0) return { created: 0, failed: 0 }
  const result = await createTasksFromTitles(titles, params)
  toastResult(result, titles[0])
  return result
}

/** Paste from the system clipboard (the ⋯ menus). Capacitor-safe read; an unreadable or empty clipboard shows "Nothing to paste". */
export async function pasteTasksFromClipboard(
  params: PasteTasksParams,
): Promise<PasteTasksResult> {
  // readClipboard() never throws: a denied or failed read comes back as "".
  const text = await readClipboard()
  if (!text.trim()) {
    toast(i18n.t("Nothing to paste"))
    return { created: 0, failed: 0 }
  }
  return pasteTasksFromText(text, params)
}
